import { useId } from "react";
import ExerciseThumbnail from "./ExerciseThumbnail";

function joinClassNames(...values) {
  return values.filter(Boolean).join(" ");
}

/*
 * A compact selectable tile: square media on the left, a label on the right,
 * the whole tile being the control.
 *
 * Deliberately business-agnostic. It knows about a label, a selected flag, a
 * disabled flag, a click handler and an optional media source — never about
 * equipment, modalities, presets, onboarding steps or persistence. Every
 * consumer keeps its own state and callbacks.
 *
 * Media. There are no dedicated equipment or cardio images yet, so the media box
 * delegates to the canonical `ExerciseThumbnail`: it reserves a square box,
 * paints the product's dumbbell placeholder when no source is supplied, and
 * fades a real image in over that same box when one is. Passing `mediaSrc`
 * later is therefore the whole of "add real photography" — the geometry never
 * moves, so a future image introduces no layout shift.
 *
 * Label. The text region is always tall enough for two lines, whether the label
 * uses one or two, and the label is centred inside it. That keeps a grid of
 * `Flat Bench` and `Selectorized Shoulder Press` on one baseline rhythm. A label
 * longer than two lines is clamped, and the full string always remains the
 * button's accessible name.
 */
export default function SelectableMediaTile({
  label,
  mediaSrc = null,
  mediaAlt = "",
  selected = false,
  disabled = false,
  onClick,
  accessibleLabel,
  className = "",
  ...props
}) {
  const generatedId = useId().replace(/:/g, "");
  const labelId = `lz-v2-media-tile-label-${generatedId}`;
  // The visible label can be clamped to two lines, so the accessible name is
  // taken from the complete string rather than from the rendered text.
  const name = accessibleLabel || (typeof label === "string" ? label : undefined);

  return (
    <button
      {...props}
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      aria-label={name}
      aria-labelledby={name ? undefined : labelId}
      className={joinClassNames(
        "lz-v2-media-tile",
        selected && "lz-v2-media-tile--selected",
        className
      )}
    >
      <span className="lz-v2-media-tile__media">
        {/*
          * `ExerciseThumbnail` fixes its own box with an inline width/height.
          * The tile owns the media size instead, so the inline style is handed
          * back to the stylesheet; the primitive's own `aspect-ratio: 1` keeps
          * the box square at whatever width the tile gives it.
          */}
        <ExerciseThumbnail
          src={mediaSrc}
          alt={mediaAlt}
          className="lz-v2-media-tile__thumbnail"
          style={{ width: "100%", height: "auto" }}
        />
      </span>
      <span className="lz-v2-media-tile__label">
        <span className="lz-v2-media-tile__label-text" id={labelId}>
          {label}
        </span>
      </span>
    </button>
  );
}
